/**
 * 内容合规性检查
 *
 * 用于防止向大模型提交不合规内容，包括：
 * - 政治敏感内容
 * - 色情淫秽内容
 * - 暴力恐怖内容
 * - 违法犯罪内容
 * - 其他违反法律法规的内容
 */

/**
 * 敏感词类别
 */
export enum SensitiveCategory {
  POLITICAL = 'political',           // 政治敏感
  PORNOGRAPHY = 'pornography',       // 色情淫秽
  VIOLENCE = 'violence',             // 暴力恐怖
  CRIME = 'crime',                   // 违法犯罪
  GAMBLING = 'gambling',             // 赌博
  DRUG = 'drug',                     // 毒品
  DISCRIMINATION = 'discrimination', // 歧视
  SPAM = 'spam',                     // 垃圾广告
}

/**
 * 敏感词检测接口
 */
export interface ComplianceResult {
  compliant: boolean;
  category?: SensitiveCategory;
  reason?: string;
  sanitized?: string;
}

/**
 * 敏感词模式列表（用于检测）
 *
 * 注意：这里只包含检测模式，不包含实际的敏感词
 * 敏感词应该通过环境变量或配置文件管理
 */
const SENSITIVE_PATTERNS = {
  // 政治敏感词检测模式（示例）
  [SensitiveCategory.POLITICAL]: [
    // 检测模式由配置文件提供
    // 这里使用占位符，实际应该从环境变量读取
  ],

  // 色情淫秽词检测模式
  // 注：使用常见违禁词的拼音或缩写，避免直接包含敏感词
  [SensitiveCategory.PORNOGRAPHY]: [
    // 检测成人内容相关词汇（使用模式匹配）
    /\u60c5\u8272|\u8272\u60c5|\u6deb\u79fd|\u6210\u4eba\u89c6\u9891|\u88f8\u804a/gi,
    // 更多模式应该从配置读取
  ],

  // 暴力恐怖词检测模式
  [SensitiveCategory.VIOLENCE]: [
    // 检测暴力恐怖相关词汇
    /\u6050\u6015\u88ad\u51fb|\u7206\u5f39\u5236\u4f5c|\u81ea\u6740\u65b9\u6cd5|\u6740\u4eba\u6559\u7a0b/gi,
    // 更多模式...
  ],

  // 违法犯罪词检测模式
  [SensitiveCategory.CRIME]: [
    // 检测违法犯罪相关词汇
    /\u8bc8\u9a97|\u6d17\u94b1|\u4f20\u9500|\u8d29\u6bd2|\u9ed1\u5ba2\u653b\u51fb/gi,
    // 更多模式...
  ],

  // 赌博相关
  [SensitiveCategory.GAMBLING]: [
    // 检测赌博相关词汇
    /\u8d4c\u535a|\u535a\u5f69|\u516d\u5408\u5f69|\u65f6\u65f6\u5f69|\u7f51\u8d4c/gi,
  ],

  // 毒品相关
  [SensitiveCategory.DRUG]: [
    // 检测毒品相关词汇
    /\u6bd2\u54c1|\u5927\u9ebb|\u6d77\u6d1b\u56e0|\u51b0\u6bd2|\u5438\u6bd2/gi,
  ],

  // 歧视性内容
  [SensitiveCategory.DISCARDIMINATION]: [
    // 检测歧视性内容
    /\u79cd\u65cf\u6b67\u89c6|\u5b97\u6559\u4ec7\u6068/gi,
    // 更多模式...
  ],

  // 垃圾广告
  [SensitiveCategory.SPAM]: [
    // 检测垃圾广告
    /\u4ee3\u5b55|\u529e\u8bc1|\u53d1\u7968|\u8d37\u6b3e|\u5957\u73b0/gi,
  ],
};

/**
 * 检查内容是否包含敏感词
 */
export function checkCompliance(content: string): ComplianceResult {
  if (!content || typeof content !== 'string') {
    return {
      compliant: true,
      reason: 'Empty or invalid content'
    };
  }

  const trimmed = content.trim();

  // 长度检查
  if (trimmed.length > 500) {
    return {
      compliant: false,
      category: SensitiveCategory.SPAM,
      reason: 'Content too long (max 500 characters)'
    };
  }

  // 遍历所有敏感类别
  for (const [category, patterns] of Object.entries(SENSITIVE_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern instanceof RegExp && pattern.test(trimmed)) {
        return {
          compliant: false,
          category: category as SensitiveCategory,
          reason: `Content contains sensitive information (${category})`
        };
      }
    }
  }

  // 检查是否为纯工具/技术查询（通常是合规的）
  const techKeywords = [
    '工具', 'tool', 'software', '应用', 'app',
    '数据库', 'database', 'api', '框架', 'framework',
    '编程', 'programming', '代码', 'code', '开发',
    'dev', '服务器', 'server', 'linux', 'docker',
    'kubernetes', 'mysql', 'redis', 'nginx', 'java',
    'python', 'javascript', 'node', 'react', 'vue',
    'mcp', 'skill', 'github', 'git', 'ci/cd'
  ];

  const hasTechKeyword = techKeywords.some(keyword =>
    trimmed.toLowerCase().includes(keyword.toLowerCase())
  );

  // 如果包含技术关键词，很可能是合规的
  if (hasTechKeyword) {
    return {
      compliant: true,
      sanitized: trimmed
    };
  }

  // 未检测到明显问题，但也不包含技术关键词
  // 返回合规但建议谨慎
  return {
    compliant: true,
    reason: 'No sensitive content detected'
  };
}

/**
 * 严格的合规性检查（用于LLM调用）
 * 比checkCompliance更严格
 */
export function strictComplianceCheck(content: string): ComplianceResult {
  const basicCheck = checkCompliance(content);

  if (!basicCheck.compliant) {
    return basicCheck;
  }

  // 额外的严格检查

  // 1. 检查是否包含绕过尝试（使用技术术语）
  // 注：检测绕过AI安全措施的常见关键词
  const bypassPatterns = [
    /bypass|jailbreak|prompt\s+injection/gi,
  ];

  for (const pattern of bypassPatterns) {
    if (pattern.test(content)) {
      return {
        compliant: false,
        category: SensitiveCategory.CRIME,
        reason: 'Detected attempt to bypass safety measures'
      };
    }
  }

  // 2. 检查是否包含个人信息
  const personalInfoPatterns = [
    /\d{15,}/, // 可能是身份证号
    /\d{11}/,   // 可能是手机号
    /@.*\.(com|cn|org|net)/gi, // 邮箱地址
  ];

  for (const pattern of personalInfoPatterns) {
    if (pattern.test(content)) {
      return {
        compliant: false,
        category: SensitiveCategory.CRIME,
        reason: 'Content may contain personal information'
      };
    }
  }

  // 3. 检查是否包含多个连续的标点符号（垃圾内容特征）
  if (/[!?！？]{3,}/.test(content)) {
    return {
      compliant: false,
      category: SensitiveCategory.SPAM,
      reason: 'Content appears to be spam'
    };
  }

  return {
    compliant: true,
    sanitized: content.trim()
  };
}

/**
 * 获取友好的错误消息
 */
export function getComplianceErrorMessage(result: ComplianceResult, lang: 'zh' | 'en' = 'zh'): string {
  if (result.compliant) {
    return '';
  }

  const messages = {
    zh: {
      [SensitiveCategory.POLITICAL]: '查询内容包含政治敏感信息，请勿提交此类内容',
      [SensitiveCategory.PORNOGRAPHY]: '查询内容包含色情淫秽信息，请勿提交此类内容',
      [SensitiveCategory.VIOLENCE]: '查询内容包含暴力恐怖信息，请勿提交此类内容',
      [SensitiveCategory.CRIME]: '查询内容包含违法犯罪信息，请勿提交此类内容',
      [SensitiveCategory.GAMBLING]: '查询内容包含赌博相关信息，请勿提交此类内容',
      [SensitiveCategory.DRUG]: '查询内容包含毒品相关信息，请勿提交此类内容',
      [SensitiveCategory.DISCARDIMINATION]: '查询内容包含歧视性内容，请勿提交此类内容',
      [SensitiveCategory.SPAM]: '查询内容疑似垃圾广告，请提交有效的技术查询',
      default: '查询内容不符合使用规范，请提交技术相关的查询'
    },
    en: {
      [SensitiveCategory.POLITICAL]: 'Query contains politically sensitive content',
      [SensitiveCategory.PORNOGRAPHY]: 'Query contains pornographic content',
      [SensitiveCategory.VIOLENCE]: 'Query contains violent or terrorist content',
      [SensitiveCategory.CRIME]: 'Query contains illegal content',
      [SensitiveCategory.GAMBLING]: 'Query contains gambling-related content',
      [SensitiveCategory.DRUG]: 'Query contains drug-related content',
      [SensitiveCategory.DISCARDIMINATION]: 'Query contains discriminatory content',
      [SensitiveCategory.SPAM]: 'Query appears to be spam',
      default: 'Query does not comply with usage guidelines'
    }
  };

  return messages[lang][result.category || 'default'] || messages[lang].default;
}

/**
 * 快速检查（用于前端实时提示）
 * 只检查明显的违规内容，不记录日志
 */
export function quickComplianceCheck(content: string): {
  valid: boolean;
  warning?: string;
} {
  if (!content || content.trim().length === 0) {
    return { valid: false, warning: 'Query cannot be empty' };
  }

  const trimmed = content.trim();

  // 快速检查明显的违规模式（使用Unicode编码）
  const obviousViolations = [
    // 成人内容
    /\u60c5\u8272|\u8272\u60c5|\u6deb\u79fd|\u88f8\u804a|\u6210\u4eba\u89c6\u9891/gi,
    // 赌博内容
    /\u8d4c\u535a|\u535a\u5f69|\u516d\u5408\u5f69|\u65f6\u65f6\u5f69|\u7f51\u8d4c/gi,
    // 毒品内容
    /\u6bd2\u54c1|\u5927\u9ebb|\u6d77\u6d1b\u56e0|\u51b0\u6bd2|\u5438\u6bd2/gi,
    // 违法犯罪
    /\u8bc8\u9a97|\u6d17\u94b1|\u4f20\u9500|\u8d29\u6bd2/gi,
  ];

  for (const pattern of obviousViolations) {
    if (pattern.test(trimmed)) {
      return {
        valid: false,
        warning: '查询内容包含不合规信息，请修改后重试'
      };
    }
  }

  return { valid: true };
}
